import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

interface BrandAsset {
  id: string;
  file_path: string;
  file_name: string;
  file_type: string;
  created_at: string;
  public_url?: string;
}

interface BrandMediaContextType {
  brandAssets: BrandAsset[];
  isLoading: boolean;
  error: Error | null;
  refreshBrandAssets: () => Promise<void>;
  deleteBrandAsset: (assetId: string, filePath: string) => Promise<void>;
}

const BrandMediaContext = createContext<BrandMediaContextType | undefined>(undefined);

export function BrandMediaProvider({ children }: { children: React.ReactNode }) {
  const [brandAssets, setBrandAssets] = useState<BrandAsset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchBrandAssets = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Fetch brand assets from the database
      const { data: assets, error: dbError } = await supabase
        .from('user_brand_assets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (dbError) throw dbError;

      // Get public URLs for each asset
      const assetsWithUrls = await Promise.all(
        assets.map(async (asset) => {
          const { data: { publicUrl } } = supabase.storage
            .from('brand-media')
            .getPublicUrl(asset.file_path);

          return {
            ...asset,
            public_url: publicUrl
          };
        })
      );

      setBrandAssets(assetsWithUrls);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch brand assets'));
    } finally {
      setIsLoading(false);
    }
  };

  const deleteBrandAsset = async (assetId: string, filePath: string) => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('brand-media')
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('user_brand_assets')
        .delete()
        .eq('id', assetId);

      if (dbError) throw dbError;

      // Refresh the assets list
      await fetchBrandAssets();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete brand asset'));
    }
  };

  useEffect(() => {
    fetchBrandAssets();
  }, []);

  return (
    <BrandMediaContext.Provider
      value={{
        brandAssets,
        isLoading,
        error,
        refreshBrandAssets: fetchBrandAssets,
        deleteBrandAsset
      }}
    >
      {children}
    </BrandMediaContext.Provider>
  );
}

export function useBrandMedia() {
  const context = useContext(BrandMediaContext);
  if (context === undefined) {
    throw new Error('useBrandMedia must be used within a BrandMediaProvider');
  }
  return context;
} 